import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = 'https://ufc-backend-nmib.onrender.com/api';

// Orange → light gray → teal gradient
// 0% = deep orange #C2460A
// 50% = light gray #C8CDD6
// 100% = deep teal #0D9488
function pctToColor(pct) {
  const t = pct;
  if (t < 0.5) {
    const u = t * 2;
    const r = Math.round(194 + (200 - 194) * u);
    const g = Math.round(70  + (205 - 70)  * u);
    const b = Math.round(10  + (214 - 10)  * u);
    return `rgb(${r},${g},${b})`;
  } else {
    const u = (t - 0.5) * 2;
    const r = Math.round(200 + (13  - 200) * u);
    const g = Math.round(205 + (148 - 205) * u);
    const b = Math.round(214 + (136 - 214) * u);
    return `rgb(${r},${g},${b})`;
  }
}

function textColor(pct) {
  // Gray middle is light so needs dark text; extremes get white
  return Math.abs(pct - 0.5) < 0.22 ? "#1a1a2e" : "#fff";
}

export default function StyleMatchupMatrix({ breakdowns1, breakdowns2, f1Name, f2Name }) {
  const [matrix, setMatrix]   = useState({});
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    axios.get(`${API}/matchup/cluster-matrix`)
      .then(res => {
        const m = {};
        res.data.forEach(row => {
          if (!m[row.f1_cluster]) m[row.f1_cluster] = {};
          m[row.f1_cluster][row.f2_cluster] = {
            pct:    parseFloat(row.win_percentage),
            sample: row.sample_size,
          };
        });
        setMatrix(m);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="matrix-loading">Loading matchup matrix...</div>;
  if (!Object.keys(matrix).length) return null;

  const top5 = (breakdowns) => {
    const data = breakdowns?.["All"] || [];
    return [...data]
      .sort((a, b) => b.percentage - a.percentage)
      .slice(0, 5)
      .map(d => d.cluster);
  };

  const f1Styles = top5(breakdowns1);
  const f2Styles = top5(breakdowns2);
  if (!f1Styles.length || !f2Styles.length) return null;

  // Break style names into 2-line display
  const splitStyle = (s) => {
    const words = s.split(' ');
    const mid   = Math.ceil(words.length / 2);
    return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
  };

  return (
    <div className="matrix-wrap">
      {/* Legend */}
      <div className="matrix-legend">
        <span className="matrix-legend-label">0%</span>
        <div className="matrix-legend-bar" />
        <span className="matrix-legend-label">100%</span>
        <span className="matrix-legend-note">F1 Win %</span>
      </div>

      <div className="matrix-scroll">
        <table className="matrix-table">
          <thead>
            {/* F2 name banner row above columns */}
            <tr>
              <th className="matrix-corner-top" />
              <th colSpan={f2Styles.length} className="matrix-f2-banner">
                <span style={{ color: 'var(--orange)' }}>{f2Name}</span> Style →
              </th>
            </tr>
            {/* Column headers */}
            <tr>
              {/* F1 name in top-left corner */}
              <th className="matrix-corner">
                ↓ <span style={{ color: 'var(--teal)' }}>{f1Name}</span> Style
              </th>
              {f2Styles.map(col => (
                <th key={col} className="matrix-col-header">
                  {splitStyle(col).map((line, i) => (
                    <span key={i} className="matrix-header-line">{line}</span>
                  ))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {f1Styles.map(row => (
              <tr key={row}>
                <td className="matrix-row-header">{row}</td>
                {f2Styles.map(col => {
                  const cell  = matrix[row]?.[col];
                  const pct   = cell?.pct ?? 0.5;
                  const bg    = pctToColor(pct);
                  const fg    = textColor(pct);
                  const isKey = f1Styles[0] === row && f2Styles[0] === col;
                  return (
                    <td
                      key={col}
                      className={`matrix-cell ${isKey ? 'matrix-key-cell' : ''}`}
                      style={{ background: bg, color: fg }}
                      onMouseEnter={() => setHovered({ row, col, pct, sample: cell?.sample })}
                      onMouseLeave={() => setHovered(null)}
                    >
                      {Math.round(pct * 100)}%
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hovered && (
        <div className="matrix-tooltip">
          <strong>{hovered.row}</strong> vs <strong>{hovered.col}</strong>
          {' — '}F1 wins <strong>{Math.round(hovered.pct * 100)}%</strong>
        </div>
      )}

      {f1Styles[0] && f2Styles[0] && matrix[f1Styles[0]]?.[f2Styles[0]] && (
        <div className="matrix-callout">
          <span className="matrix-callout-f1">{f1Styles[0]}</span>
          <span className="matrix-callout-sep"> vs </span>
          <span className="matrix-callout-f2">{f2Styles[0]}</span>
          <span className="matrix-callout-pct">
            {' — '}{Math.round(matrix[f1Styles[0]][f2Styles[0]].pct * 100)}% historical win rate for F1
          </span>
        </div>
      )}
    </div>
  );
}
